/**
 * Shared character combat statistics.
 * Combines class base (level growth) + General + class talents + equipment.
 * One accumulator — no second stat engine.
 *
 * Flat Attack insertion (M7-D2 Stage B, approved option C):
 *   combo = floor((weaponSwing + faceSum) × comboMultiplier)
 *   pre%  = combo + classFlatAttack
 *   then applyOutgoingDamageBonuses(pre%) → Vulnerable → Crit → …
 * Class flat Attack is never combo-multiplied or OH-penalised.
 */

import {
  deriveClassBaseStats,
  type DerivedClassBaseStats,
} from '../data/classBaseStats';
import {
  VANGUARD_TALENT_NODES,
  type TalentEffectKind,
} from '../data/vanguardTalents';
import type { ClassId } from '../persistence/schema';
import {
  accumulateGeneralEffectTotals,
  deriveDiceBonusesFromGeneralRanks,
} from './generalTalents';
import { listUnlockedActiveSkillIds, ranksOf } from './talents';

/**
 * Legacy constant — equals Vanguard L1 baseMaxHp.
 * Prefer deriveClassBaseStats for new code. Kept for tests / UI labels.
 */
export const BASE_PLAYER_MAX_HP = 40;

/**
 * @deprecated Class inherent flat Armour replaces a universal base rating.
 * Kept as 0 export for legacy imports; combat uses class + gear flats.
 */
export const BASE_ARMOUR_RATING = 0;

/**
 * OPEN: base critical damage bonus percent (150% total = +50% over non-crit).
 * Crit damage talents add percentage points on top of this.
 */
export const BASE_CRIT_DAMAGE_BONUS_PCT = 50;

/**
 * Equipment stance / affix context for the shared combat-stats pipeline.
 * Build via `buildEquipmentContextFromLoadout` (domain/equipmentCombat.ts)
 * from a dungeon-entry snapshot (or current loadout on the Hero screen).
 */
export interface EquipmentContext {
  twoHanded?: boolean;
  dualWielding?: boolean;
  offHandIsDagger?: boolean;
  /** Any off-hand equipped (shield, weapon, or focus). */
  hasOffHand?: boolean;
  /** Flat armour from inherent gear + supported affixes. */
  flatArmour?: number;
  /** Flat max-HP bonus from supported gear affixes. */
  flatMaxHp?: number;
  /** Additive attack-damage % from supported gear affixes. */
  attackDamageBonusPct?: number;
  /** Additive crit chance percentage points from gear. */
  critChancePp?: number;
  /** Additive block chance percentage points from gear. */
  blockChancePp?: number;
  /** Additive dodge chance percentage points from gear. */
  dodgeChancePp?: number;
  /**
   * Precomputed weapon swing contribution (main + penalised off-hand).
   * Added to die face sum before combo multiplier in combat.
   */
  weaponSwingDamage?: number;
  weaponSwingMainHand?: number;
  /** Off-hand contribution after dual-wield penalty (0 if not dual-wielding). */
  weaponSwingOffHand?: number;
  /** Equipped special die instance id, if any. */
  specialDieInstanceId?: string | null;
}

export interface CombatStatsOptions {
  classId?: ClassId;
  /** Permanent class level (1–CLASS_LEVEL_CAP). Defaults to 1. */
  classLevel?: number;
}

export interface CharacterCombatStats {
  maxHp: number;
  /** Sum of attack_pct + attack_damage_pct + gear %. Applied additively. */
  attackDamageBonusPct: number;
  /**
   * effectiveArmourRating = totalFlatArmour × (1 + armourBonusPct/100).
   * totalFlat = class + equipment (+ affixes).
   */
  armourRating: number;
  /** Additive Armour % from talents (General + class). */
  armourBonusPct: number;
  /**
   * Class-derived flat Attack (base + level growth).
   * Added once after combo floor, before Attack % (option C).
   */
  flatAttack: number;
  /** Class-derived flat Armour (base + level growth), before gear. */
  classFlatArmour: number;
  /** Equipment flat Armour (inherent + affixes), before class. */
  equipmentFlatArmour: number;
  /** Total flat Armour before % (class + equipment). */
  totalFlatArmour: number;
  /** Class id / level used to derive flats (for UI / save debug). */
  classId: ClassId;
  classLevel: number;
  critChancePp: number;
  /** Total crit damage bonus % (base + talents). */
  critDamageBonusPct: number;
  bleedChancePp: number;
  bleedDamagePct: number;
  damageVsBelow50HpPct: number;
  damageVsBelow25HpPct: number;
  damageVsBleedingPct: number;
  blockChancePp: number;
  /** GDD: dodge negates eligible attack damage and statuses. */
  dodgeChancePp: number;
  counterChancePp: number;
  counterChanceWhenBlockingPp: number;
  counterDamageAfterBlockingPct: number;
  chanceToAttackAgainPp: number;
  /** Combat turn budgets (base M3 + General dice talents). */
  fullRerollsPerTurn: number;
  targetedRerollsPerTurn: number;
  specialDieSlotUnlocked: boolean;
  /**
   * Skirmisher Crit Chance on Consecutive Hits (+1 pp / rank, max 3).
   * Accumulates per consecutive crit already landed in the current attack
   * sequence (see attackSequence.ts — CONSECUTIVE_CRIT_STACKING_MODE).
   */
  critChanceOnConsecutiveHitsPp: number;
  /** Equipment-gated — applied only when EquipmentContext enables them. */
  twoHandedWeaponDamagePct: number;
  twoHandedAffixStatsPct: number;
  dualWieldDamagePct: number;
  offHandDaggerDamagePct: number;
  offHandAffixStatsPct: number;
  unlockedActiveSkillIds: readonly string[];
  /**
   * Weapon swing damage locked into this stats snapshot (dungeon entry).
   * Added to die face sum before combo multiplier. 0 = unarmed fallback.
   */
  weaponSwingDamage: number;
  weaponSwingMainHand: number;
  weaponSwingOffHand: number;
}

function emptyAccum(): Record<TalentEffectKind, number> {
  const keys: TalentEffectKind[] = [
    'max_hp_pct',
    'armour_pct',
    'attack_pct',
    'attack_damage_pct',
    'crit_chance_pp',
    'crit_damage_pct',
    'bleed_chance_pp',
    'bleed_damage_pct',
    'damage_vs_below_50_hp_pct',
    'damage_vs_below_25_hp_pct',
    'damage_vs_bleeding_pct',
    'two_handed_weapon_damage_pct',
    'two_handed_affix_stats_pct',
    'dual_wield_damage_pct',
    'off_hand_dagger_damage_pct',
    'off_hand_affix_stats_pct',
    'counterattack_chance_pp',
    'crit_chance_on_consecutive_hits_pp',
    'chance_to_attack_again_pp',
    'block_chance_pp',
    'counterattack_chance_when_blocking_pp',
    'counterattack_damage_after_blocking_pct',
    'unlock_active_skill',
  ];
  return Object.fromEntries(keys.map((k) => [k, 0])) as Record<
    TalentEffectKind,
    number
  >;
}

export function accumulateTalentEffectTotals(
  talentRanks: Record<string, number>,
): Record<TalentEffectKind, number> {
  const totals = emptyAccum();
  for (const node of VANGUARD_TALENT_NODES) {
    const ranks = ranksOf(talentRanks, node.id);
    if (ranks <= 0) continue;
    totals[node.effect.kind] += node.effect.perRank * ranks;
  }
  return totals;
}

/**
 * Pure calculator: class base (level) + class talents + General + equipment.
 * Attack % applies once to (comboDamage + flatAttack) after the combo floor.
 * Armour % stacks additively into effectiveArmourRating.
 */
export function computeCharacterCombatStats(
  classTalentRanks: Record<string, number> = {},
  equipment: EquipmentContext = {},
  generalTalentRanks: Record<string, number> = {},
  options: CombatStatsOptions = {},
): CharacterCombatStats {
  const classId = options.classId ?? 'vanguard';
  const classLevel = options.classLevel ?? 1;
  const classBase: DerivedClassBaseStats = deriveClassBaseStats(
    classId,
    classLevel,
  );

  const t = accumulateTalentEffectTotals(classTalentRanks);
  const g = accumulateGeneralEffectTotals(generalTalentRanks);
  const dice = deriveDiceBonusesFromGeneralRanks(generalTalentRanks);

  const hpPct = t.max_hp_pct + g.max_hp_pct;
  const maxHpFromClassAndTalents = Math.floor(
    classBase.classMaxHp * (1 + hpPct / 100),
  );
  const maxHp = Math.max(
    1,
    maxHpFromClassAndTalents + Math.max(0, equipment.flatMaxHp ?? 0),
  );

  let attackDamageBonusPct =
    t.attack_pct +
    t.attack_damage_pct +
    g.attack_pct +
    (equipment.attackDamageBonusPct ?? 0);
  if (equipment.twoHanded) {
    attackDamageBonusPct += t.two_handed_weapon_damage_pct;
  }
  if (equipment.dualWielding) {
    attackDamageBonusPct += t.dual_wield_damage_pct;
  }
  if (equipment.offHandIsDagger) {
    attackDamageBonusPct += t.off_hand_dagger_damage_pct;
  }

  const armourBonusPct = t.armour_pct + g.armour_pct;
  const classFlatArmour = classBase.flatArmour;
  const equipmentFlatArmour = Math.max(0, equipment.flatArmour ?? 0);
  const totalFlatArmour = classFlatArmour + equipmentFlatArmour;
  const armourRating = Math.max(
    0,
    totalFlatArmour * (1 + armourBonusPct / 100),
  );

  return {
    maxHp,
    attackDamageBonusPct,
    armourRating,
    armourBonusPct,
    flatAttack: classBase.flatAttack,
    classFlatArmour,
    equipmentFlatArmour,
    totalFlatArmour,
    classId,
    classLevel: classBase.classLevel,
    critChancePp:
      t.crit_chance_pp + g.crit_chance_pp + (equipment.critChancePp ?? 0),
    critDamageBonusPct:
      BASE_CRIT_DAMAGE_BONUS_PCT + t.crit_damage_pct + g.crit_damage_pct,
    bleedChancePp: t.bleed_chance_pp,
    bleedDamagePct: t.bleed_damage_pct,
    damageVsBelow50HpPct: t.damage_vs_below_50_hp_pct,
    damageVsBelow25HpPct: t.damage_vs_below_25_hp_pct,
    damageVsBleedingPct: t.damage_vs_bleeding_pct,
    blockChancePp:
      t.block_chance_pp + g.block_chance_pp + (equipment.blockChancePp ?? 0),
    dodgeChancePp: g.dodge_chance_pp + (equipment.dodgeChancePp ?? 0),
    counterChancePp: t.counterattack_chance_pp + g.counterattack_chance_pp,
    counterChanceWhenBlockingPp: t.counterattack_chance_when_blocking_pp,
    counterDamageAfterBlockingPct: t.counterattack_damage_after_blocking_pct,
    chanceToAttackAgainPp: t.chance_to_attack_again_pp,
    fullRerollsPerTurn: dice.fullRerollsPerTurn,
    targetedRerollsPerTurn: dice.targetedRerollsPerTurn,
    specialDieSlotUnlocked: dice.specialDieSlotUnlocked,
    critChanceOnConsecutiveHitsPp: t.crit_chance_on_consecutive_hits_pp,
    twoHandedWeaponDamagePct: t.two_handed_weapon_damage_pct,
    twoHandedAffixStatsPct: t.two_handed_affix_stats_pct,
    dualWieldDamagePct: t.dual_wield_damage_pct,
    offHandDaggerDamagePct: t.off_hand_dagger_damage_pct,
    offHandAffixStatsPct: t.off_hand_affix_stats_pct,
    unlockedActiveSkillIds: listUnlockedActiveSkillIds(classTalentRanks),
    weaponSwingDamage: Math.max(0, Math.floor(equipment.weaponSwingDamage ?? 0)),
    weaponSwingMainHand: Math.max(
      0,
      Math.floor(equipment.weaponSwingMainHand ?? 0),
    ),
    weaponSwingOffHand: Math.max(
      0,
      Math.floor(equipment.weaponSwingOffHand ?? 0),
    ),
  };
}

export interface OutgoingHitContext {
  targetHp: number;
  targetMaxHp: number;
  targetIsBleeding: boolean;
}

/**
 * Apply additive attack-damage % and conditional % to a base damage amount.
 * Spec: percentage attack-damage bonuses stack additively.
 */
export function applyOutgoingDamageBonuses(
  baseDamage: number,
  stats: CharacterCombatStats,
  hit: OutgoingHitContext,
): number {
  if (baseDamage <= 0) return 0;
  let bonus = stats.attackDamageBonusPct;
  const ratio = hit.targetMaxHp > 0 ? hit.targetHp / hit.targetMaxHp : 1;
  if (ratio < 0.5) {
    bonus += stats.damageVsBelow50HpPct;
  }
  if (ratio < 0.25) {
    bonus += stats.damageVsBelow25HpPct;
  }
  if (hit.targetIsBleeding) {
    bonus += stats.damageVsBleedingPct;
  }
  return Math.max(0, Math.floor((baseDamage * (100 + bonus)) / 100));
}

/**
 * Crit roll for one attack. Returns final damage and whether it crit.
 * Chance = base critChancePp + optional extras (consecutive-crit talent, Precise Twin Strike, …), clamped 0–100.
 */
export function rollCritDamage(
  damage: number,
  stats: CharacterCombatStats,
  roll01: number,
  options?: { extraCritChancePp?: number },
): { damage: number; isCrit: boolean } {
  if (damage <= 0) return { damage: 0, isCrit: false };
  const chancePp = Math.max(
    0,
    Math.min(100, stats.critChancePp + (options?.extraCritChancePp ?? 0)),
  );
  const isCrit = roll01 < chancePp / 100;
  if (!isCrit) return { damage, isCrit: false };
  return {
    damage: Math.max(
      0,
      Math.floor(damage * (1 + stats.critDamageBonusPct / 100)),
    ),
    isCrit: true,
  };
}

/**
 * Incoming mitigation (M7-D2).
 * effectiveArmourRating = totalFlatArmour * (1 + armourBonusPct/100) (computed upstream).
 * Rating formula when rating > 0; otherwise no Armour mitigation (OPEN DR retired).
 * Cap: at most 75% damage reduction.
 */
export function mitigateIncomingDamage(
  rawDamage: number,
  stats: CharacterCombatStats,
): number {
  if (rawDamage <= 0) return 0;
  const rating = Math.max(0, stats.armourRating);
  if (rating <= 0) {
    return Math.max(0, Math.floor(rawDamage));
  }
  const mitigated = Math.floor(rawDamage * (100 / (100 + rating)));
  const floorAt75Dr = Math.ceil(rawDamage * 0.25);
  return Math.max(floorAt75Dr, Math.max(0, mitigated));
}

/** Spec bleed: 1% max HP per stack per turn, minimum 2 damage PER STACK. */
export function bleedDamageForStacks(
  stacks: number,
  maxHp: number,
  bleedDamagePctBonus = 0,
): number {
  if (stacks <= 0) return 0;
  const perStack = Math.max(2, Math.floor(maxHp * 0.01));
  const raw = perStack * stacks;
  if (bleedDamagePctBonus === 0) return raw;
  return Math.max(0, Math.floor((raw * (100 + bleedDamagePctBonus)) / 100));
}

export function defaultCharacterCombatStats(): CharacterCombatStats {
  return computeCharacterCombatStats({});
}
